import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  User, 
  Building2, 
  Phone, 
  Mail, 
  Settings, 
  CheckCircle, 
  Sparkles, 
  Facebook, 
  Instagram, 
  Linkedin 
} from 'lucide-react';
import emailjs from '@emailjs/browser';
import './consultation.css';

gsap.registerPlugin(ScrollTrigger);

const Consultation = () => {
  const [formData, setFormData] = useState({
    clientType: 'company',
    name: '',
    companyName: '',
    contactNumber: '',
    email: '',
    services: []
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const featuresRef = useRef(null);
  const formRef = useRef(null);
  const orbsRef = useRef([]);

  const serviceOptions = [
    { value: 'amazon', label: 'Amazon Advertising' },
    { value: 'meta', label: 'Meta (Facebook/Instagram)' },
    { value: 'google', label: 'Google Ads' },
    { value: 'bing', label: 'Bing Ads' },
    { value: 'custom', label: 'Custom Solution' }
  ];

  // GSAP Animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      orbsRef.current.forEach((orb, index) => {
        if (orb) {
          gsap.to(orb, { rotation: 360, duration: 20 + index * 5, repeat: -1, ease: "none" });
          gsap.to(orb, { x: Math.sin(index * 60) * 50, y: Math.cos(index * 60) * 30, duration: 8 + index * 2, repeat: -1, yoyo: true, ease: "sine.inOut" });
        }
      });

      if (headerRef.current) {
        gsap.fromTo(headerRef.current.children, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1, stagger: 0.2, scrollTrigger: { trigger: headerRef.current, start: "top 80%" } });
      }

      if (featuresRef.current) {
        gsap.fromTo(featuresRef.current.children, { opacity: 0, x: -50, scale: 0.8 }, { opacity: 1, x: 0, scale: 1, duration: 0.8, stagger: 0.2, scrollTrigger: { trigger: featuresRef.current, start: "top 80%" } });
      }

      if (formRef.current) {
        gsap.fromTo(formRef.current, { opacity: 0, x: 50 }, { opacity: 1, x: 0, duration: 1, scrollTrigger: { trigger: formRef.current, start: "top 80%" } });
      }

      gsap.to(".floating-particles", { backgroundPosition: "0% -100%", duration: 20, repeat: -1, ease: "none" });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Inject Calendly inline widget after submission
  useEffect(() => {
    if (isSubmitted) {
      // Add Calendly CSS
      const link = document.createElement("link");
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      link.rel = "stylesheet";
      document.head.appendChild(link);

      // Add Calendly container
      const calendlyContainer = document.createElement("div");
      calendlyContainer.className = "calendly-inline-widget";
      calendlyContainer.setAttribute("data-url", "https://calendly.com/admin-beyondtheanalysis/30min");
      calendlyContainer.style.minWidth = "320px";
      calendlyContainer.style.height = "700px";

      // Append below success message
      const successMessage = document.querySelector(".success-message");
      if (successMessage) successMessage.appendChild(calendlyContainer);

      // Load Calendly script
      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, [isSubmitted]);

  // Handle Input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (serviceValue) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceValue)
        ? prev.services.filter(s => s !== serviceValue)
        : [...prev.services, serviceValue]
    }));
  };

  // Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    gsap.to(e.target.querySelector('.consultation-submit'), { scale: 0.95, duration: 0.1, yoyo: true, repeat: 1 });

    const templateParams = {
      client_type: formData.clientType,
      name: formData.clientType === 'company' ? formData.companyName : formData.name,
      contact_number: formData.contactNumber,
      email: formData.email,
      services: formData.services.join(', ')
    };

    try {
      await emailjs.send('service_lf52r03', '12345678', templateParams, 'zoajfA_usiK_NoqxU');
      setIsSubmitted(true);
      setIsSubmitting(false);

      gsap.fromTo('.success-message', { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.8, ease: "back.out(1.7)" });
      if (sectionRef.current) sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      console.error('EmailJS Error:', err);
      setIsSubmitting(false);
      alert('Failed to send. Please try again.');
    }
  };

  // Success Screen
  if (isSubmitted) {
    return (
      <section ref={sectionRef} className="consultation-section">
        <div className="consultation-background">
          <div className="gradient-mesh">
            {[0,1,2,3].map(i => <div key={i} ref={el => orbsRef.current[i] = el} className={`mesh-gradient mesh-${i+1}`}></div>)}
          </div>
          <div className="grid-pattern"></div>
          <div className="floating-particles"></div>
          <div className="noise-texture"></div>
        </div>
        <div className="consultation-container">
          <div className="success-message">
            <div className="success-animation">
              <CheckCircle className="success-icon" />
              <div className="success-ripple"></div>
            </div>
            <h2 className="success-title"><span className="highlight-word">Thank You!</span></h2>
            <p className="success-subtitle">
              We've received your consultation request. Our team will contact you within 24 hours.
            </p>
            <p className="success-subtitle"> our 30-min solution call — book it now.</p>
            <div className="social-links">
           <div className="centerit">
              <div className="social-icons centerit">
                <a href="https://www.facebook.com/profile.php?id=61560885657463" target="_blank" rel="noopener noreferrer"><Facebook /></a>
                <a href="https://www.instagram.com/beyondtheanalysis/" target="_blank" rel="noopener noreferrer"><Instagram /></a>
                <a href="https://www.linkedin.com/company/beyond-the-analysis/about/?viewAsMember=true" target="_blank" rel="noopener noreferrer"><Linkedin /></a>
              </div>
           </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Normal Form
  return (
    <section ref={sectionRef} className="consultation-section">
      <div className="consultation-background">
        <div className="gradient-mesh">{[0,1,2,3].map(i => <div key={i} ref={el => orbsRef.current[i] = el} className={`mesh-gradient mesh-${i+1}`}></div>)}</div>
        <div className="grid-pattern"></div>
        <div className="floating-particles"></div>
        <div className="noise-texture"></div>
      </div>
      <div className="consultation-container">
        <div ref={headerRef} className="consultation-header">
          <div className="achievement-badge"><Sparkles className="badge-icon" /><span className="badge-text">Free Strategy Session</span></div>
          <h1 className="consultation-title"><span className="animate-word">Get Your</span> <span className="highlight-word">Free</span> <span className="premium-word">Consultation</span></h1>
          <p className="consultation-subtitle">
            Transform your digital presence with data-driven strategies. Get personalized insights from Beyond The Analysis experts.
          </p>
        </div>

        <div className="consultation-content">
          <div ref={featuresRef} className="consultation-features">
            <h3 className="features-title">What You'll Get:</h3>
            <div className="features-list">
              <div className="feature-card"><div className="feature-icon-wrapper"><Settings /></div><div className="feature-content"><h4>Strategic Analysis</h4><p>Comprehensive audit of your current digital marketing efforts and untapped growth opportunities</p></div></div>
              <div className="feature-card"><div className="feature-icon-wrapper"><CheckCircle /></div><div className="feature-content"><h4>Custom Roadmap</h4><p>Tailored action plan designed to achieve your specific business objectives and KPIs</p></div></div>
              <div className="feature-card"><div className="feature-icon-wrapper"><Sparkles /></div><div className="feature-content"><h4>Expert Insights</h4><p>Industry best practices and cutting-edge strategies from our data analytics specialists</p></div></div>
            </div>
          </div>

          <form ref={formRef} className="consultation-form" onSubmit={handleSubmit}>
            {/* Form fields */}
            <div className="form-section">
              <h3 className="form-section-title">Tell us about yourself</h3>
              <div className="form-group">
                <label className="form-label">I am representing:</label>
                <div className="radio-group">
                  <label className={`radio-option ${formData.clientType==='company'?'selected':''}`}><input type="radio" name="clientType" value="company" checked={formData.clientType==='company'} onChange={handleInputChange} /><Building2 className="radio-icon" /><span>A Company</span></label>
                  <label className={`radio-option ${formData.clientType==='person'?'selected':''}`}><input type="radio" name="clientType" value="person" checked={formData.clientType==='person'} onChange={handleInputChange} /><User className="radio-icon" /><span>Myself/Individual</span></label>
                </div>
              </div>
              {formData.clientType==='company'?
                <div className="form-group"><label htmlFor="companyName" className="form-label">Company Name</label><input type="text" id="companyName" name="companyName" value={formData.companyName} onChange={handleInputChange} className="form-input" required placeholder="Enter your company name"/></div> :
                <div className="form-group"><label htmlFor="name" className="form-label">Your Name</label><input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className="form-input" required placeholder="Enter your full name"/></div>
              }
              <div className="form-row">
                <div className="form-group"><label htmlFor="contactNumber" className="form-label"><Phone className="label-icon"/> Contact Number</label><input type="tel" id="contactNumber" name="contactNumber" value={formData.contactNumber} onChange={handleInputChange} className="form-input" required placeholder="+91 7570007967"/></div>
                <div className="form-group"><label htmlFor="email" className="form-label"><Mail className="label-icon"/> Email Address</label><input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className="form-input" required placeholder="your.email@company.com"/></div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Services of Interest</h3>
              <p className="form-section-subtitle">Select all that apply</p>
              <div className="services-grid">
                {serviceOptions.map(service=>(
                  <label key={service.value} className={`service-option ${formData.services.includes(service.value)?'selected':''}`}>
                    <input type="checkbox" checked={formData.services.includes(service.value)} onChange={()=>handleServiceChange(service.value)} />
                    <div className="service-content"><CheckCircle className="service-check"/><span className="service-label">{service.label}</span></div>
                  </label>
                ))}
              </div>
            </div>

            <button type="submit" className="cta-button primary large consultation-submit" disabled={isSubmitting||formData.services.length===0}><Sparkles />{isSubmitting?'Submitting...':'Get Free Consultation'}<div className="button-glow"></div></button>

            <p className="contact-info">Questions? Email us at <a href="mailto:marketing@beyondtheanalysis.com">marketing@beyondtheanalysis.com</a> or call <a href="tel:+917570007967">+91 7570007967</a></p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Consultation;
